/**
 * BACKUP (JSON) DER PROJEKTVERWALTUNG — volle Projektzeilen statt Listenauszug.
 *
 * ─── Was falsch war ─────────────────────────────────────────────────────────
 * „Backup (JSON)" in AdminProjectList schrieb die Eintraege der Projektliste
 * in die Datei. Ein Listeneintrag ist ein AUSZUG (listProject.ts): von `data`
 * nur `metadata`. Alles andere im Blob — Unterlage, Vorankuendigung,
 * Einstellungen, Gefaehrdungen, Sicherheitspunkte, Unterlage-Versionen — fehlte,
 * die Datei taugte also nicht als Sicherung.
 *
 * ─── Was die Datei jetzt enthaelt ───────────────────────────────────────────
 * Je Projekt der gewaehlten Liste EINE Zeile aus `projects`, so wie sie in der
 * Datenbank steht (`select('*')`): alle Spalten, darunter das VOLLE `data`,
 * dazu die Leistungspositionen als Rohzeilen (`project_services`, eingebettet)
 * sowie `exportDate` und `exportedBy`. Die Datei bleibt ein Array von
 * Projektobjekten mit denselben Spaltenschluesseln wie bisher.
 *
 * ─── Was sie NICHT enthaelt ─────────────────────────────────────────────────
 * Der Speicherweg (saveProjectToCloudIntern) nimmt einiges aus dem Blob heraus
 * und legt es in Kindtabellen ab; diese Zeilen sind nicht in der Datei:
 *   • Maengel samt Fotos und Abschlussbild  → `inspection_items`
 *     (die Bilder selbst liegen im Storage, die Zeilen tragen nur Verweise)
 *   • Checklisten                           → `project_checklists`
 *   • Notizen                               → `project_notes`
 *   • Plaene                                → `project_plans`
 *   • Anhangbilder der Unterlage            → `ufsa_appendix_images`
 *   • RAB-Eintraege der Unterlage           → `ufsa_rab_entries`
 *   • Beteiligte als Zeilen                 → `project_contacts`
 *     (der Verteiler steht zusaetzlich im Blob, `metadata.distributionList`)
 * Im Blob stehen deshalb `metadata.globalData.services`/`notes` und
 * `metadata.plans` leer — die Positionen liegen in `project_services` daneben.
 * Die Verknuepfung Mutter/Tochter steht in den Spalten `parent_project_id`,
 * `linked_billing_mode`, `linked_billing_history` (Nr. 71), nicht im Blob.
 *
 * ─── Warum eine eigene Abfrage und nicht `loadProjectById` ──────────────────
 * `loadProjectById` je Projekt hiesse neun Abfragen pro Projekt, die
 * Base64-Selbstheilung (schreibt!) und vor allem einen neuen Speicher-Anker je
 * Projekt (`rememberProjectVersion`): ein Projekt, das im selben Tab offen
 * ist, bekaeme den Anker des frischen Serverstands, und sein naechstes
 * Speichern ueberschriebe eine fremde Aenderung ohne Konfliktmeldung. Ein
 * Backup darf nichts schreiben und nichts verstellen — es LIEST nur.
 *
 * Gebuendelt nach Kennung (`.in('id', …)`) wie die Projektliste
 * (doLoadProjectsFromCloud): kein `.range()`, das sich bei einer Loeschung
 * verschieben koennte, und kleine Antworten — mit vollem `data` sind die Zeilen
 * deutlich groesser als in der Liste, und grosse Antworten brechen ueber
 * HTTP/3 mitten im Body ab. Nacheinander statt parallel: das Backup ist kein
 * Eilfall, und der Fortschritt zaehlt dann ehrlich.
 *
 * Nie zurueck in den App-Zustand geben und nie speichern: die Zeilen sind rohe
 * Datenbankzeilen, kein geladenes Projekt (Vorfall 21.10.2026).
 */
import { supabase } from './supabaseClient';
import { alsAbfrageFehler } from './spaltenRueckfall';

/** Kennungen je Abfrage. Kleiner als in der Liste (50): hier kommt `data` ganz. */
export const BACKUP_BUENDEL = 10;

/** Versuche je Buendel — einer mehr fuer den Abbruch mitten im Body. */
export const BACKUP_VERSUCHE = 2;

/** Eine rohe Zeile aus `projects` samt eingebetteten `project_services`. */
export type BackupZeile = { id: string } & Record<string, unknown>;

/** Ein Eintrag der Backup-Datei. */
export type BackupEintrag = BackupZeile & { exportDate: string; exportedBy: string | undefined };

/** Laedt die Zeilen zu einem Buendel Kennungen; wirft bei einem Fehler. */
export type BackupBuendelLader = (kennungen: string[]) => Promise<BackupZeile[]>;

export interface BackupErgebnis {
    /** In der Reihenfolge der uebergebenen Kennungen, ohne Dubletten. */
    zeilen: BackupZeile[];
    /** Kennungen ohne Zeile — inzwischen geloescht oder nicht mehr lesbar. */
    fehlend: string[];
}

/** Dateiname wie bisher: `Projekte_Backup_JJJJ-MM-TT.json`. */
export const backupDateiname = (jetzt: Date): string =>
    `Projekte_Backup_${jetzt.toISOString().slice(0, 10)}.json`;

/** Schneidet die Kennungen in Buendel fester Groesse. */
export const schneideInBuendel = (kennungen: string[], groesse: number = BACKUP_BUENDEL): string[][] => {
    const schritt = Math.max(1, Math.floor(groesse));
    const buendel: string[][] = [];
    for (let i = 0; i < kennungen.length; i += schritt) buendel.push(kennungen.slice(i, i + schritt));
    return buendel;
};

/**
 * Die Zeilen in die Reihenfolge der Auswahl bringen (die Datei soll so
 * geordnet sein wie die Tabelle, aus der sie kommt) und benennen, was fehlt.
 * Doppelte Kennungen zaehlen einmal.
 */
export const ordneBackupZeilen = (kennungen: string[], zeilen: BackupZeile[]): BackupErgebnis => {
    const nachId = new Map<string, BackupZeile>();
    for (const zeile of zeilen) {
        if (zeile && typeof zeile.id === 'string' && !nachId.has(zeile.id)) nachId.set(zeile.id, zeile);
    }
    const geordnet: BackupZeile[] = [];
    const fehlend: string[] = [];
    const gesehen = new Set<string>();
    for (const id of kennungen) {
        if (gesehen.has(id)) continue;
        gesehen.add(id);
        const zeile = nachId.get(id);
        if (zeile) geordnet.push(zeile);
        else fehlend.push(id);
    }
    return { zeilen: geordnet, fehlend };
};

/** Die Dateieintraege: die rohe Zeile plus Exportzeitpunkt und -person, wie bisher. */
export const baueBackupInhalt = (
    zeilen: BackupZeile[],
    stempel: { exportDate: string; exportedBy: string | undefined },
): BackupEintrag[] =>
    zeilen.map(zeile => ({ ...zeile, exportDate: stempel.exportDate, exportedBy: stempel.exportedBy }));

/** Ein Buendel aus der Datenbank — nur lesend, ohne Anker, ohne Selbstheilung. */
const ladeBuendelAusDatenbank: BackupBuendelLader = async (kennungen) => {
    // `*` statt einer Spaltenliste: ein Backup soll alles tragen, was die Zeile
    // hat, und `*` kann keine fehlende Spalte (42703) ausloesen.
    const { data, error } = await supabase
        .from('projects')
        .select('*, project_services(*)')
        .in('id', kennungen);
    if (error) throw alsAbfrageFehler(error);
    return (data || []) as BackupZeile[];
};

/**
 * Laedt die vollen Projektzeilen zu den Kennungen, Buendel fuer Buendel.
 *
 * Scheitert ein Buendel auch im zweiten Versuch, scheitert das ganze Backup:
 * eine Datei, in der still ein Buendel fehlt, sieht vollstaendig aus und ist
 * es nicht. Fehlt dagegen nur eine einzelne Zeile (geloescht seit dem Laden
 * der Liste), steht sie in `fehlend` — das ist kein Fehler, sondern der
 * heutige Stand.
 */
export const ladeBackupZeilen = async (
    kennungen: string[],
    optionen: {
        onFortschritt?: (geladen: number, gesamt: number) => void;
        lader?: BackupBuendelLader;
        buendelGroesse?: number;
    } = {},
): Promise<BackupErgebnis> => {
    const lader = optionen.lader ?? ladeBuendelAusDatenbank;
    const eindeutig = Array.from(new Set(kennungen));
    const buendel = schneideInBuendel(eindeutig, optionen.buendelGroesse ?? BACKUP_BUENDEL);
    const gesamt = eindeutig.length;
    const zeilen: BackupZeile[] = [];
    let geladen = 0;
    optionen.onFortschritt?.(0, gesamt);
    for (const teil of buendel) {
        let letzterFehler: unknown = null;
        let antwort: BackupZeile[] | null = null;
        for (let versuch = 0; versuch < BACKUP_VERSUCHE && antwort === null; versuch++) {
            try {
                antwort = await lader(teil);
            } catch (e) {
                letzterFehler = e;
            }
        }
        if (antwort === null) throw letzterFehler ?? new Error('Abfrage fehlgeschlagen');
        zeilen.push(...antwort);
        geladen += teil.length;
        optionen.onFortschritt?.(geladen, gesamt);
    }
    return ordneBackupZeilen(eindeutig, zeilen);
};

/** Die Fehlermeldung fuer die Oberflaeche — mit dem Grund, wenn einer da ist. */
export const backupFehlerText = (e: unknown): string => {
    const grund = (e as { message?: unknown })?.message;
    const zusatz = typeof grund === 'string' && grund.trim() ? ` (${grund.trim()})` : '';
    return `Das Backup konnte nicht erstellt werden${zusatz}. Es wurde keine Datei heruntergeladen — bitte erneut versuchen.`;
};

/** Der Hinweis, wenn einzelne Projekte nicht mehr geladen werden konnten. */
export const backupFehlendText = (namen: string[]): string => {
    const anzahl = namen.length;
    const liste = namen.slice(0, 5).join(', ') + (anzahl > 5 ? ` und ${anzahl - 5} weitere` : '');
    return anzahl === 1
        ? `Backup heruntergeladen, aber ein Projekt fehlt darin: ${liste}. `
            + 'Es wurde seit dem Laden der Liste gelöscht oder ist nicht mehr lesbar.'
        : `Backup heruntergeladen, aber ${anzahl} Projekte fehlen darin: ${liste}. `
            + 'Sie wurden seit dem Laden der Liste gelöscht oder sind nicht mehr lesbar.';
};
